import { createUploader, type UploadedPdf } from './ui/uploader'
import {
  createConverterOptions,
  type ConverterOptionsState,
} from './ui/converter-options'
import { createProgressDisplay } from './ui/progress'
import { createResultDisplay } from './ui/result'
import { showError } from './ui/error'
import {
  convertPdf,
  ConversionCancelledError,
  type ConversionResult,
  type PageErrorContext,
  type PageErrorAction,
} from './converter/conversion-controller'
import { CanvasSizeError } from './converter/pdf-renderer'

type Screen = 'uploader' | 'options' | 'progress' | 'result'

export function initApp(root: HTMLElement): void {
  let currentUploaded: UploadedPdf | null = null
  let cancelled = false

  function showScreen(screen: Screen): void {
    switch (screen) {
      case 'uploader':
        if (currentUploaded) {
          currentUploaded.pdf.destroy()
        }
        currentUploaded = null
        showUploader()
        break
      case 'options':
        if (currentUploaded) showOptions(currentUploaded)
        break
      case 'progress':
        break
      case 'result':
        break
    }
  }

  function showUploader(): void {
    createUploader(root, (uploaded: UploadedPdf) => {
      currentUploaded = uploaded
      showOptions(uploaded)
    })
  }

  function showOptions(uploaded: UploadedPdf): void {
    createConverterOptions(
      root,
      uploaded,
      (state: ConverterOptionsState) => {
        void startConversion(uploaded, state)
      },
    )
  }

  async function startConversion(
    uploaded: UploadedPdf,
    options: ConverterOptionsState,
  ): Promise<void> {
    cancelled = false

    const updateProgress = createProgressDisplay(root, () => {
      cancelled = true
    })

    try {
      const result: ConversionResult = await convertPdf(
        uploaded.pdf,
        uploaded.filename,
        options,
        {
          onProgress: updateProgress,
          shouldCancel: () => cancelled,
          onPageError: (ctx) => showPageErrorDialog(root, ctx),
        },
      )

      await createResultDisplay(root, result, () => {
        showScreen('uploader')
      })
    } catch (err) {
      if (err instanceof ConversionCancelledError) {
        if (currentUploaded) showOptions(currentUploaded)
      } else {
        console.error(err)
        showError(root, getErrorMessage(err))
        if (currentUploaded) {
          setTimeout(() => {
            if (currentUploaded) showOptions(currentUploaded)
          }, 2500)
        }
      }
    }
  }

  function showPageErrorDialog(
    root: HTMLElement,
    ctx: PageErrorContext,
  ): Promise<PageErrorAction> {
    return new Promise((resolve) => {
      const overlay = document.createElement('div')
      overlay.className = 'page-error-overlay'
      overlay.innerHTML = `
        <div class="page-error-dialog" role="alertdialog" aria-labelledby="pageErrorTitle" aria-describedby="pageErrorMsg">
          <h3 class="page-error-title" id="pageErrorTitle">Page ${ctx.pageNumber} couldn\u2019t be converted</h3>
          <p class="page-error-message" id="pageErrorMsg"></p>
          <div class="page-error-actions">
            <button type="button" class="page-error-btn page-error-btn--retry" id="retryBtn">Retry</button>
            <button type="button" class="page-error-btn page-error-btn--skip" id="skipBtn">Skip page</button>
            <button type="button" class="page-error-btn page-error-btn--cancel" id="cancelBtn">Cancel</button>
          </div>
        </div>
      `

      const msgEl = overlay.querySelector<HTMLParagraphElement>('#pageErrorMsg')!
      msgEl.textContent = ctx.message

      root.appendChild(overlay)

      const retryBtn = overlay.querySelector<HTMLButtonElement>('#retryBtn')!
      const skipBtn = overlay.querySelector<HTMLButtonElement>('#skipBtn')!
      const cancelBtn = overlay.querySelector<HTMLButtonElement>('#cancelBtn')!

      function close(action: PageErrorAction): void {
        overlay.remove()
        resolve(action)
      }

      retryBtn.addEventListener('click', () => close('retry'))
      skipBtn.addEventListener('click', () => close('skip'))
      cancelBtn.addEventListener('click', () => close('cancel'))

      retryBtn.focus()
    })
  }

  function getErrorMessage(err: unknown): string {
    if (err instanceof CanvasSizeError) {
      return 'This PDF page is too large for your browser at the selected resolution. Try Smart HD.'
    }
    const msg = err instanceof Error ? err.message : String(err)
    if (msg.includes('out of memory') || msg.toLowerCase().includes('oom')) {
      return 'Your device doesn\u2019t have enough available memory to convert this PDF at the selected quality. Try Smart HD or convert fewer pages.'
    }
    return 'Something went wrong during conversion. Please try again.'
  }

  showScreen('uploader')
}
